import * as React from "react";
import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { ChevronDown } from "lucide-react";

import { cn } from "@/lib/utils";

const Accordion = AccordionPrimitive.Root;

const AccordionItem = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item ref={ref} className={cn("border-b", className)} {...props} />
));
AccordionItem.displayName = "AccordionItem";

const AccordionTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Header className="flex">
    <AccordionPrimitive.Trigger
      ref={ref}
      className={cn(
        "flex flex-1 items-center justify-between py-4 text-sm font-medium transition-all hover:underline [&[data-state=open]>svg]:rotate-180",
        className
      )}
      {...props}
    >
      {children}
      <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200" />
    </AccordionPrimitive.Trigger>
  </AccordionPrimitive.Header>
));
AccordionTrigger.displayName = "AccordionTrigger";

const ACCORDION_ANIMATION_PREFIX = "accordion-";

const isAccordionAnimation = (event: React.AnimationEvent<HTMLDivElement>): boolean =>
  event.animationName.startsWith(ACCORDION_ANIMATION_PREFIX);

const AccordionContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => {
  // Only clip while the accordion is actively animating open/closed. At rest,
  // dropping `overflow-hidden` lets touch-drag chain up to the page scroller
  // on Android WebView; otherwise the wrapper acts as a scroll container and
  // swallows the gesture.
  const [isAnimating, setIsAnimating] = React.useState(false);

  const handleAnimationStart = React.useCallback((event: React.AnimationEvent<HTMLDivElement>) => {
    if (isAccordionAnimation(event)) setIsAnimating(true);
  }, []);

  const handleAnimationEnd = React.useCallback((event: React.AnimationEvent<HTMLDivElement>) => {
    if (isAccordionAnimation(event)) setIsAnimating(false);
  }, []);

  return (
    <AccordionPrimitive.Content
      ref={ref}
      onAnimationStart={handleAnimationStart}
      onAnimationEnd={handleAnimationEnd}
      className={cn(
        "text-sm data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down",
        isAnimating && "overflow-hidden",
        className
      )}
      {...props}
    >
      <div className={cn("pb-4 pt-0", className)}>{children}</div>
    </AccordionPrimitive.Content>
  );
});
AccordionContent.displayName = "AccordionContent";

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
